import { useState, useContext, useEffect, useRef } from 'react';
import { Typography, Tag, Form, Input, message, DatePicker } from 'antd';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import localeData from 'dayjs/plugin/localeData';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { GlobalContext } from 'soxo-bootstrap-core';
import AppDrawer from '../../../../../components/drawer/drawer';
import { Appointments } from '../../../../../models';
import { formatToDDMMYYYY} from '../../../../../utils/date.utils';
import { ActionButton, parseSlotExp } from '../rendering-components';
import BlockedSubmitArrow from '/src/assets/icons/BlockedSubmitArrow.png';
import './block-slot-drawer.scss';

dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(customParseFormat);

export default function BlockSlotDrawer({
    open,
    onClose,
    selectedPackage,
    selectedSlots,
    setSelectedSlots,
    selectedDate,
    setSelectedDate,
    form,
    setShouldFetch,
}) {
    const inputRef = useRef(null);
    const [drawerWidth, setDrawerWidth] = useState(500);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 768) {
                setDrawerWidth('90%');
            } else {
                setDrawerWidth(500);
            }
        };

        window.addEventListener('resize', handleResize);
        handleResize(); // Set initial width

        return () => window.removeEventListener('resize', handleResize);
    }, []);


    const [loading, setLoading] = useState(false);

    const { settings } = useContext(GlobalContext);

    // if -1 → user must choose expiry manually
    const slotSettings = settings?.SLOTSET;
    const expiryTime = slotSettings?.expiryTime;

    const handleSubmit = async () => {
        if (loading) return; // Prevent duplicate API calls while loading is true
        try {
            const values = await form.validateFields();

            if (!selectedSlots?.length) {
                message.warning('Please select at least one slot');
                return;
            }
            if (!selectedDate) {
                message.warning('Please select a date');
                return;
            }

            setLoading(true);

            const payloads = selectedSlots.map((slot) => ({
                token_no: slot.item.token_no,
                app_time: slot.item.as_starttime,
                app_date: formatToDDMMYYYY(selectedDate),
                remarks: values.remarks,
                reason: values.reason,
                mode: 'TEMP',
                source: 'DESK',
                submode: 'SLTBK',
                expiry_time: dayjs(values.expiry).format('HH:mm'),
                expiry_date: formatToDDMMYYYY(values.expiry), // <-- expiry based on picker,
                package_code: selectedPackage?.itm_desc || '',
            }));

            const response = await Appointments.multipleSlotBlock(payloads);

            if (!response?.success) {
                throw new Error('Slot blocking failed');
            }

            message.success('Slots blocked successfully!');
            form.resetFields();
            setSelectedSlots([]);
            setSelectedDate(null);
            // toggle, not just true
            setShouldFetch((prev) => !prev);
            // close drawer after success
            onClose();
        } catch (err) {
            console.error('Error blocking slots:', err);
            message.error('Failed to block slots. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const getInitialExpiry = () => {
        if (!selectedDate) return null;
        const now = dayjs(); // current date & time

        if (expiryTime === -1) {
            // manual → just return current date & time
            return now;
        } else {
            // auto → current date & time + expiryTime
            const minutes = parseSlotExp(expiryTime);
            return now.add(minutes, 'minute');
        }
    };
    // Validator to check for non-empty, non-whitespace strings
    // Usage: { validator: noEmptyOrSpaces('Error message here') }

    const noEmptyOrSpaces = (message) => ({
        validator: (_, value) => (value && value.trim() !== '' ? Promise.resolve() : Promise.reject(new Error(message))),
    });
    // Set expiry field when drawer opens or when date/slots change
    useEffect(() => {
        if (open) {
            form.setFieldsValue({
                expiry: getInitialExpiry(),
            });
        }
    }, [open, selectedDate, selectedSlots]);

    useEffect(() => {
        if (open) {
            setTimeout(() => {
                inputRef.current?.focus();
            }, 200); // Slight delay for animation
        }
    }, [open]);
    return (
        <AppDrawer
            title="Block Slot Temporary"
            placement="right"
            width={drawerWidth}
            onClose={onClose}
            maskClosable={false}
            visible={open}
            className="block-slot-drawer"
        >
            {/* Info Text */}
            <p style={{ color: '#19374E' }}>Fill in Guest Details to Block Slot Temporarily</p>

            {/* Package Card */}
            {selectedPackage && (
                <div className="package-card-reserved">
                    {selectedPackage.image && <img src={selectedPackage.image} alt="pkg" />}
                    <div className="package-info">
                        <div className="package-name">{selectedPackage.name || 'Selected Package'}</div>
                        <div className="package-price">₹{selectedPackage.price?.toLocaleString() || '—'}</div>
                    </div>
                </div>
            )}

            {/* Blocked Slots */}
            <div className="blocked-slots">
                <Typography.Text strong>Blocked Slot</Typography.Text>
                <div className="slots-container">
                    {selectedSlots?.map((s, idx) => (
                        <Tag
                            key={s.item.as_starttime}
                            closable={selectedSlots?.length > 1}
                            onClose={() => {
                                const updated = selectedSlots.filter((va, i) => i !== idx);
                                setSelectedSlots(updated);
                                if (updated.length === 0) setSelectedDate(null);
                            }}
                        >
                            {s.item.as_starttime}
                        </Tag>
                    ))}
                </div>
            </div>

            {/* Form */}
            <Form form={form} layout="vertical">
                <Form.Item
                    label="Reason for Blocking"
                    name="reason"
                    rules={[
                        // { required: true, message: 'Please enter reason' },
                        noEmptyOrSpaces('Please enter reason'),
                    ]}
                >
                    {/* <Input placeholder="Enter here"  />
                     */}
                    <Input
                        placeholder="Enter here"
                        key={open ? 'open' : 'closed'} //Force re-render every time drawer opens
                        ref={inputRef}
                    />
                </Form.Item>

                <Form.Item
                    label="Remarks"
                    name="remarks"
                    rules={[
                        // { required: true, message: 'Please enter remarks' },
                        noEmptyOrSpaces('Please enter remarks'),
                    ]}
                >
                    <Input placeholder="Enter here" />
                </Form.Item>

                {/* Expiry Date & Time */}
                <Form.Item
                    label="Expiry Date & Time"
                    name="expiry"
                    rules={[
                        {
                            required: true,
                            message: 'Please select expiry date & time',
                        },
                    ]}
                    initialValue={getInitialExpiry()}
                >
                    <DatePicker
                        showTime={{ format: 'HH:mm' }}
                        format="YYYY-MM-DD HH:mm"
                        style={{ width: '100%' }}
                        placeholder="Select expiry date & time"
                        disabledDate={(current) => selectedDate && current.format('YYYY-MM-DD') !== dayjs(selectedDate).format('YYYY-MM-DD')}
                        disabled={expiryTime !== -1}
                    />
                </Form.Item>

                <ActionButton
                    type="primary"
                    label="Submit"
                    icon={BlockedSubmitArrow}
                    loading={loading}
                    block
                    onClick={handleSubmit}
                    disabled={loading}
                />
            </Form>
        </AppDrawer>
    );
}
